import { SOURCE_TYPE } from '@/types/feedbackConst';

import { SCRAPER_NAME } from '../types';
import { scraperConfigMap, type ScraperConfigs, SourceScraperMap } from '.';
import {
  FieldPropertyMap,
  type SCRAPER_REQ_FIELD_KEY,
} from './scraper-field-map/field-const';

/**
 * item_per_day를 사용하는 스크래퍼 목록
 * listening query create와 맞물리는 스크래퍼는 일일 수집 제한을 사용한다.
 * 나머지 스크래퍼는 max_items를 사용한다.
 */
const ITEM_PER_DAY_SCRAPERS: SCRAPER_NAME[] = [
  SCRAPER_NAME.TiktokPostByKeywordForSyncly,
  SCRAPER_NAME.TiktokPostByProfile,
  SCRAPER_NAME.InstagramCommentByKeyword,
  SCRAPER_NAME.InstagramCommentByProfile,
  SCRAPER_NAME.InstagramReelsByKeywordForSyncly,
  SCRAPER_NAME.RedditPostByKeyword,
  SCRAPER_NAME.YoutubeShortsByKeywordForSyncly,
  SCRAPER_NAME.TwitterPostByKeyword,
  SCRAPER_NAME.FacebookCommentByKeyword,
];

export const getIsItemPerDayField = (scraperName?: SCRAPER_NAME): boolean => {
  if (!scraperName) return false;
  return ITEM_PER_DAY_SCRAPERS.includes(scraperName);
};

export const scraperOptionNameMap = new Map<SCRAPER_NAME, string>([
  [SCRAPER_NAME.FacebookCommentByKeyword, 'Keyword'],
  [SCRAPER_NAME.FacebookPrivateGroup, 'Private group'],
  [SCRAPER_NAME.FacebookCommentByUrl, 'Url'],
  [SCRAPER_NAME.InstagramCommentByKeyword, 'Keyword'],
  [SCRAPER_NAME.InstagramCommentByProfile, 'Profile'],
  [SCRAPER_NAME.InstagramReelsByKeywordForSyncly, 'Keyword'],
  [SCRAPER_NAME.NaverCafeAllSearch, 'All cafe'],
  [SCRAPER_NAME.NaverSpecificCafeSearch, 'Specific cafe'],
  [SCRAPER_NAME.NaverCafeAccountBase, 'Account base'],
  [SCRAPER_NAME.RedditPostByKeyword, 'Keyword'],
  [SCRAPER_NAME.TiktokPostByKeywordForSyncly, 'Post (Keyword)'],
  [SCRAPER_NAME.TiktokPostByProfile, 'Post (Profile)'],
  [SCRAPER_NAME.TiktokCommentByKeywordForSyncly, 'Comment (Keyword)'],
  [SCRAPER_NAME.TiktokCommentByProfile, 'Comment (Profile)'],
  [SCRAPER_NAME.TwitterPostByKeyword, 'Keyword'],
  [SCRAPER_NAME.YoutubeShortsByKeywordForSyncly, 'Keyword'],
]);

export const getScraperOptionName = (scraper_name: SCRAPER_NAME): string => {
  return scraperOptionNameMap.get(scraper_name) || scraper_name;
};

const SCRAPER_NAME_ALIASES: Partial<Record<string, SCRAPER_NAME>> = {
  [SCRAPER_NAME.NaverCafeInSearch]: SCRAPER_NAME.NaverSpecificCafeSearch,
};

export const canonicalizeScraperName = (
  scraperName?: string | null,
): SCRAPER_NAME | null => {
  if (!scraperName) {
    return null;
  }

  if (scraperConfigMap.has(scraperName as SCRAPER_NAME)) {
    return scraperName as SCRAPER_NAME;
  }

  const aliasedScraperName = SCRAPER_NAME_ALIASES[scraperName];

  if (aliasedScraperName && scraperConfigMap.has(aliasedScraperName)) {
    return aliasedScraperName;
  }

  return null;
};

export const getScraperConfigSafe = (
  scraperName?: string | null,
): ScraperConfigs | null => {
  const normalizedScraperName = canonicalizeScraperName(scraperName);

  if (!normalizedScraperName) {
    return null;
  }

  return scraperConfigMap.get(normalizedScraperName);
};

export const getSourceByScraperName = (
  scraper_name: SCRAPER_NAME,
): SOURCE_TYPE => {
  const scraperConfig = getScraperConfigSafe(scraper_name);
  if (!scraperConfig) throw new Error(`Invalid scraper name: ${scraper_name}`);
  return scraperConfig.source;
};

export const getSourceByScraperNameSafe = (
  scraperName?: string | null,
): SOURCE_TYPE | null => {
  const normalizedScraperName = canonicalizeScraperName(scraperName);

  if (!normalizedScraperName) {
    return null;
  }

  return getSourceByScraperName(normalizedScraperName);
};

const SOURCE_TYPE_BY_DIRECT_KEY: Partial<Record<string, SOURCE_TYPE>> = {
  fmkorea: SOURCE_TYPE.FMKOREA,
  'google-news': SOURCE_TYPE.GOOGLE_NEWS,
  'naver-blog': SOURCE_TYPE.NAVER_BLOG,
  'naver-cafe': SOURCE_TYPE.NAVER_CAFE,
  ppomppu: SOURCE_TYPE.PPOMPPU,
  'dc-inside': SOURCE_TYPE.DC_INSIDE,
};

export const getSourceByScraperNameOrSourceKey = (
  scraperName: SCRAPER_NAME | string,
): SOURCE_TYPE => {
  const directSourceType = SOURCE_TYPE_BY_DIRECT_KEY[scraperName];

  if (directSourceType) {
    return directSourceType;
  }

  const resolvedSourceType = getSourceByScraperNameSafe(scraperName);

  if (!resolvedSourceType) {
    throw new Error(`Invalid scraper name or source key: ${scraperName}`);
  }

  return resolvedSourceType;
};

export const getScraperOptionsBySource = (source_type: SOURCE_TYPE) => {
  const options = SourceScraperMap.get(source_type);
  if (!options) throw new Error(`Invalid source type: ${source_type}`);
  if (options.length === 0)
    throw new Error(`No scraper options found for source type: ${source_type}`);
  return options;
};

export const getScraperOptionsBySourceSafe = (
  sourceType: SOURCE_TYPE,
): SCRAPER_NAME[] => {
  if (!SourceScraperMap.has(sourceType)) {
    return [];
  }

  return SourceScraperMap.get(sourceType);
};

const DEFAULT_MAX_ITEM_COUNT = 30000;
export const getMaxItemCountByScraper = (
  scraperName?: SCRAPER_NAME,
): number => {
  if (!scraperName) return DEFAULT_MAX_ITEM_COUNT;
  const scraperConfig = getScraperConfigSafe(scraperName);
  return scraperConfig?.maxItems || DEFAULT_MAX_ITEM_COUNT;
};

export const getScraperFieldList = (scraperName: SCRAPER_NAME) => {
  const normalizedScraperName = canonicalizeScraperName(scraperName);

  if (!normalizedScraperName) {
    return [];
  }

  return getScraperConfigSafe(normalizedScraperName)?.fields || [];
};

export const getFieldProperty = (
  field: SCRAPER_REQ_FIELD_KEY,
  scraperName: SCRAPER_NAME,
) => {
  const baseProperty = FieldPropertyMap.get(field);
  if (!baseProperty) {
    return undefined;
  }

  // Get scraper-specific config
  const scraperConfig = getScraperConfigSafe(scraperName)?.customFieldConfig;
  const fieldOverride = scraperConfig?.get(field);

  // Merge base property with scraper-specific overrides
  if (fieldOverride) {
    return {
      ...baseProperty,
      ...fieldOverride,
    };
  }

  return baseProperty;
};
